<script>
// NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import NextButton from 'dashboard/components-next/button/Button.vue';
// NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import InboxOptionMenu from './InboxOptionMenu.vue';
import InboxDisplayMenu from './InboxDisplayMenu.vue';

// NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
// Distance between a trigger and the menu it opens, and the smallest gap we
// allow between the menu and the edge of the viewport.
const MENU_OFFSET = 4;
const VIEWPORT_PADDING = 8;

export default {
  components: {
    NextButton,
    // NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
    TeleportWithDirection,
    InboxOptionMenu,
    InboxDisplayMenu,
  },
  props: {
    isContextMenuOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['redirect', 'filter'],
  data() {
    return {
      showInboxDisplayMenu: false,
      showInboxOptionMenu: false,
      // NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
      displayMenuStyle: {},
      optionMenuStyle: {},
    };
  },
  // NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
  computed: {
    ...mapGetters({ isRTL: 'accounts/isRTL' }),
  },
  watch: {
    isContextMenuOpen: {
      handler(val) {
        if (val) {
          this.showInboxDisplayMenu = false;
          this.showInboxOptionMenu = false;
        }
      },
      immediate: true,
    },
  },
  // NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
  mounted() {
    window.addEventListener('resize', this.repositionOpenMenu);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.repositionOpenMenu);
  },
  // end NOTICE: KAPTION-INTEGRATION
  methods: {
    // NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
    /**
     * Both menus are rendered into <body> rather than beside their trigger.
     *
     * The inbox list card carries `wp-clip`, which under Warm Paper is
     * `overflow: hidden` (assets/scss/_warm-paper.scss), and the card is capped
     * at `lg:max-w-[340px]`. An absolutely positioned menu inside it was cropped
     * at the card's edge: labels were cut off mid-word, and a click on the
     * hidden part of an item fell through to the pane underneath, so "Mark all
     * as read" did nothing unless the few visible pixels were hit. Teleporting
     * escapes that clip; `position: fixed` then places the menu against the
     * viewport, which is safe here because no ancestor establishes a
     * containing block (no transform/filter/will-change).
     *
     * @param {HTMLElement} trigger element the menu belongs to
     * @param {'start'|'end'} align which of the trigger's inline edges to follow
     */
    menuStyleFor(trigger, align) {
      if (!trigger) return {};
      const { bottom, left, right } = trigger.getBoundingClientRect();
      const top = bottom + MENU_OFFSET;
      // `start`/`end` are logical: in RTL the inline-start edge is the right one.
      const anchorRight = align === 'start' ? this.isRTL : !this.isRTL;
      if (anchorRight) {
        return {
          top: `${top}px`,
          right: `${Math.max(VIEWPORT_PADDING, window.innerWidth - right)}px`,
        };
      }
      return { top: `${top}px`, left: `${Math.max(VIEWPORT_PADDING, left)}px` };
    },
    repositionOpenMenu() {
      if (this.showInboxDisplayMenu) {
        this.displayMenuStyle = this.menuStyleFor(
          this.$refs.displayTrigger?.$el,
          'start'
        );
      }
      if (this.showInboxOptionMenu) {
        this.optionMenuStyle = this.menuStyleFor(
          this.$refs.optionTrigger?.$el,
          'end'
        );
      }
    },
    // end NOTICE: KAPTION-INTEGRATION
    markAllRead() {
      useTrack(INBOX_EVENTS.MARK_ALL_NOTIFICATIONS_AS_READ);
      // NOTICE: KAPTION-INTEGRATION - Tell the user when the request fails
      // POSTs notifications/read_all through the store action, which re-throws
      // on failure. Without this catch a failed request was an unhandled
      // rejection and the user saw nothing at all.
      this.$store
        .dispatch('notifications/readAll')
        .then(() => {
          useAlert(this.$t('INBOX.ALERTS.MARK_ALL_READ'));
        })
        .catch(() => {
          useAlert(this.$t('INBOX.ALERTS.MARK_ALL_READ_ERROR'));
        });
    },
    deleteAll() {
      // NOTICE: KAPTION-INTEGRATION - Tell the user when the request fails
      this.$store
        .dispatch('notifications/deleteAll')
        .then(() => {
          useAlert(this.$t('INBOX.ALERTS.DELETE_ALL'));
        })
        .catch(() => {
          useAlert(this.$t('INBOX.ALERTS.DELETE_ALL_ERROR'));
        });
    },
    deleteAllRead() {
      // NOTICE: KAPTION-INTEGRATION - Tell the user when the request fails
      this.$store
        .dispatch('notifications/deleteAllRead')
        .then(() => {
          useAlert(this.$t('INBOX.ALERTS.DELETE_ALL_READ'));
        })
        .catch(() => {
          useAlert(this.$t('INBOX.ALERTS.DELETE_ALL_READ_ERROR'));
        });
    },
    openInboxDisplayMenu() {
      this.showInboxDisplayMenu = !this.showInboxDisplayMenu;
      // NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
      if (this.showInboxDisplayMenu) {
        this.displayMenuStyle = this.menuStyleFor(
          this.$refs.displayTrigger?.$el,
          'start'
        );
      }
    },
    openInboxOptionsMenu() {
      this.showInboxOptionMenu = !this.showInboxOptionMenu;
      // NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card
      if (this.showInboxOptionMenu) {
        this.optionMenuStyle = this.menuStyleFor(
          this.$refs.optionTrigger?.$el,
          'end'
        );
      }
    },
    onInboxOptionMenuClick(key) {
      const actions = {
        mark_all_read: () => this.markAllRead(),
        delete_all: () => this.deleteAll(),
        delete_all_read: () => this.deleteAllRead(),
      };
      const action = actions[key];
      if (action) action();
      // NOTICE: KAPTION-INTEGRATION - Close the menu once an option is chosen
      // Menu items stop click propagation, so click-away never fired and the
      // menu stayed open over the list after the action ran.
      this.showInboxOptionMenu = false;
      this.$emit('redirect');
    },
    onFilterChange(option) {
      this.$emit('filter', option);
      this.showInboxDisplayMenu = false;
      this.$emit('redirect');
    },
  },
};
</script>

<template>
  <div class="flex items-center justify-between w-full gap-1 h-12 px-3">
    <div class="flex items-center gap-2 min-w-0 flex-1">
      <h1 class="min-w-0 text-base font-medium truncate text-n-slate-12">
        {{ $t('INBOX.LIST.TITLE') }}
      </h1>
      <div class="relative">
        <!-- NOTICE: KAPTION-INTEGRATION - ref anchors the teleported display menu -->
        <NextButton
          ref="displayTrigger"
          :label="$t('INBOX.LIST.DISPLAY_DROPDOWN')"
          icon="i-lucide-chevron-down"
          trailing-icon
          slate
          xs
          faded
          @click="openInboxDisplayMenu"
        />
      </div>
    </div>
    <div class="relative flex items-center gap-1">
      <!-- NOTICE: KAPTION-INTEGRATION - ref anchors the teleported options menu -->
      <NextButton
        ref="optionTrigger"
        icon="i-lucide-sliders-vertical"
        slate
        xs
        faded
        @click="openInboxOptionsMenu"
      />
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - Warm Paper inbox menus escape the clipped card.
         The inbox card is `wp-clip` (overflow: hidden under Warm Paper), so the
         menus render into <body>, fixed to the viewport, instead of absolutely
         inside this header. See menuStyleFor. -->
    <TeleportWithDirection v-if="showInboxDisplayMenu" to="body">
      <InboxDisplayMenu
        v-on-clickaway="openInboxDisplayMenu"
        class="fixed z-[9999]"
        :style="displayMenuStyle"
        @filter="onFilterChange"
      />
    </TeleportWithDirection>

    <TeleportWithDirection v-if="showInboxOptionMenu" to="body">
      <InboxOptionMenu
        v-on-clickaway="openInboxOptionsMenu"
        class="fixed z-[9999]"
        :style="optionMenuStyle"
        @option-click="onInboxOptionMenuClick"
      />
    </TeleportWithDirection>
    <!-- end NOTICE: KAPTION-INTEGRATION -->
  </div>
</template>
